import { useEffect, useState } from "react";
import { Building2, User } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { NewContactDialog } from "@/features/contacts/NewContactDialog";
import { NewCompanyDialog } from "@/features/companies/NewCompanyDialog";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Forwarded to the person/company dialogs — see NewLeadButton's Props. */
  confirmNavigate?: () => boolean;
}

type Mode = "choose" | "person" | "company";

/**
 * Lead-type chooser shown before creating a new lead: "is this a person or a
 * company?" (B2C vs B2B). Picking one hands off to the existing contact/company
 * creation dialog so both flows stay in one place. Mirrors the meeting ask to
 * branch the new-lead experience by subject type.
 */
export function NewLeadDialog({ open, onClose, confirmNavigate }: Props) {
  const [mode, setMode] = useState<Mode>("choose");

  // Always start at the chooser when (re)opened.
  useEffect(() => {
    if (open) setMode("choose");
  }, [open]);

  if (open && mode === "person") {
    return (
      <NewContactDialog
        open
        onClose={onClose}
        defaultLeadType="b2c"
        confirmNavigate={confirmNavigate}
      />
    );
  }
  if (open && mode === "company") {
    return <NewCompanyDialog open onClose={onClose} confirmNavigate={confirmNavigate} />;
  }

  return (
    <Dialog open={open && mode === "choose"} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New lead</DialogTitle>
          <DialogDescription>Is this lead a person or a company?</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setMode("person")}
            className="flex flex-col items-start gap-2 rounded-lg border p-4 text-left transition-colors hover:border-primary hover:bg-accent"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
              <User className="h-5 w-5" />
            </span>
            <span className="font-medium">Person</span>
            <span className="text-xs text-muted-foreground">
              An individual contact (B2C) — best when the person is the customer.
            </span>
          </button>

          <button
            type="button"
            onClick={() => setMode("company")}
            className="flex flex-col items-start gap-2 rounded-lg border p-4 text-left transition-colors hover:border-primary hover:bg-accent"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Building2 className="h-5 w-5" />
            </span>
            <span className="font-medium">Company</span>
            <span className="text-xs text-muted-foreground">
              A business / account (B2B) — the company is the lead; add contacts under it.
            </span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
